import React, { useContext, useState } from "react";

import { Funder, Recommender } from "@engine/round/aggregate";

import { StateContext } from "@client/pages/round/Round";
import { getUnevaluatedEvaluableGroups } from "@engine/round/accessors";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { sortByProperty } from "@lib/utils/sortByProperty";
import clsx from "clsx";
import { AllocationChart } from "../AllocationChart/AllocationChart";
import { FrontendEvaluation } from "../useEvaluatorEvaluationsCache";
import AllocationChartHeader from "./AllocationChartHeader";
import { Evaluables } from "./Evaluables";

export const SidebarRoot = ({
  children,
  userId,
}: {
  children: React.ReactNode;
  userId?: string;
}) => {
  return (
    <div
      className="flex flex-none flex-col h-full min-w-[350px] w-[20%]"
      key={userId}
    >
      {children}
    </div>
  );
};

type TabOptions = "allocations" | "evaluables";

export const EvaluatorSidebar = ({
  evaluator,
  evaluations,
  unilateralAllocations,
}: {
  evaluator: Recommender | Funder;
  evaluations: FrontendEvaluation[];
  unilateralAllocations: {
    [evaluableId: string]: { amount: number };
  };
}) => {
  // Default to proposals tab if there are no evaluations (hold proposal is not included)
  const [selectedTab, setSelectedTab] = useState<TabOptions>(
    evaluations.length > 1 ? "allocations" : "evaluables",
  );
  const { round } = useContext(StateContext);

  const {
    recused: recusedEvaluables,
    notStarted: notStartedEvaluables,
    hidden: hiddenEvaluables,
    deferred: deferredEvaluables,
    evaluated: evaluatedEvaluables,
    canceled: canceledEvaluables,
    zeroed: zeroedEvaluables,
  } = getUnevaluatedEvaluableGroups(round, evaluator);

  const unusedEvaluables = notStartedEvaluables.concat(canceledEvaluables);

  // evaluations.sort(sortByProperty("shortname"));
  evaluatedEvaluables.sort(sortByProperty("shortname"));
  unusedEvaluables.sort(sortByProperty("shortname"));
  recusedEvaluables.sort(sortByProperty("shortname"));
  hiddenEvaluables.sort(sortByProperty("shortname"));
  deferredEvaluables.sort(sortByProperty("shortname"));
  zeroedEvaluables.sort(sortByProperty("shortname"));

  function selectTab(tabOption: TabOptions) {
    setSelectedTab(tabOption);
  }

  const allocations = Object.entries(unilateralAllocations).map(
    ([evaluableId, { amount }]) => {
      const evaluation = evaluations.find((e) => e.evaluableId === evaluableId);
      return { evaluableId, allocation: amount, evaluation };
    },
  );

  function renderTab() {
    switch (selectedTab) {
      case "allocations":
        return (
          <AllocationChart evaluator={evaluator} allocations={allocations} />
        );
      case "evaluables":
        return (
          <Evaluables
            evaluator={evaluator}
            allocations={allocations}
            evaluatedEvaluables={evaluatedEvaluables}
            recusedEvaluables={recusedEvaluables}
            unusedEvaluables={unusedEvaluables}
            hiddenEvaluables={hiddenEvaluables}
            deferredEvaluables={deferredEvaluables}
            zeroedEvaluables={zeroedEvaluables}
          />
        );
      default:
        assertUnreachable(selectedTab);
    }
  }
  const getEvaluablesTabLabel = (evaluatorType: string) => {
    switch (evaluatorType) {
      case "recommender":
        return "Proposals";
      case "funder":
        return "Recommenders";
      default:
        return "Evaluables";
    }
  };

  return (
    <SidebarRoot userId={evaluator.memberId}>
      <AllocationChartHeader evaluator={evaluator} />
      <div className="flex border-b-blue-400 bg-stone-200 dark:bg-inherit">
        <SidebarTab
          label="Allocations"
          isActive={selectedTab === "allocations"}
          onClick={() => selectTab("allocations")}
        />
        <SidebarTab
          label={getEvaluablesTabLabel(evaluator.type)}
          isActive={selectedTab === "evaluables"}
          onClick={() => selectTab("evaluables")}
        />
      </div>
      {renderTab()}
    </SidebarRoot>
  );
};

export const SidebarTab = ({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      className={clsx(
        "flex-1 text-center cursor-pointer",
        isActive && "bg-blue-400",
      )}
      onClick={onClick}
    >
      {label}
    </div>
  );
};
